// Ассеты мира: иконки/бейджи инфраструктуры, угроз, преемственности
// и вывод slug'ов ассетов. Вынесено из CampaignSetupPage.tsx без
// изменения поведения (Фаза 1, разбор монолита).

import { useState } from "react";
import { type CampaignContinuityItem, type CampaignInfrastructureOverview } from "../api";
import { GAME_UI_ASSET_BASE } from "./shared";

type WorldAssetKind = "goal" | "resource" | "threat" | "indicator" | "institution" | "faction" | "system";
type AssetBadgeKind = WorldAssetKind | "dossier";
const WORLD_ASSET_FALLBACKS: Record<AssetBadgeKind, string> = {
  goal: "icon_room_world.png",
  resource: "icon_resource.png",
  threat: "icon_threat.png",
  indicator: "icon_ui_status.png",
  institution: "icon_room_council.png",
  faction: "icon_agreement.png",
  system: "icon_room_world.png",
  dossier: "icon_dossier.png",
};
function inferWorldAssetSlug(kind: WorldAssetKind, code?: string, label?: string) {
  const token = `${code ?? ""} ${label ?? ""}`.toLowerCase();
  if (kind === "goal" || kind === "system") return kind;
  if (kind === "institution") {
    if (/(health|med|clinic|hospital|doctor|medicine|мед|больн|леч)/.test(token)) return "healthcare";
    if (/(security|guard|order|defen|milit|patrol|страж|оборон|поряд)/.test(token)) return "security";
    if (/(supply|logistic|transport|warehouse|water|energy|food|склад|снабж|логист|постав|вод|энерг)/.test(token)) return "logistics";
    return "governance";
  }
  if (kind === "faction") {
    if (/(worker|producer|maker|engineer|specialist|труд|рабоч|мастер|инженер|специал)/.test(token)) return "workers";
    if (/(guard|militia|army|soldier|defen|security|страж|охран|обор)/.test(token)) return "guards";
    if (/(outsider|refugee|nomad|trader|visitor|внеш|чуж|бежен|кочев|гость)/.test(token)) return "outsiders";
    return "citizens";
  }
  return worldAssetSlug(code || label);
}
function infrastructureCategoryAsset(category: string) {
  switch (category) {
    case "HOUSING":
      return { kind: "resource" as const, code: "housing" };
    case "FOOD":
      return { kind: "resource" as const, code: "food" };
    case "MEDICINE":
      return { kind: "resource" as const, code: "medicine" };
    case "ENERGY":
      return { kind: "resource" as const, code: "energy" };
    case "WATER":
      return { kind: "resource" as const, code: "water" };
    case "ORDER":
    case "DEFENSE":
      return { kind: "resource" as const, code: "security" };
    case "KNOWLEDGE":
      return { kind: "resource" as const, code: "information" };
    case "PRODUCTION":
      return { kind: "resource" as const, code: "materials" };
    case "TRANSPORT":
    case "GOVERNANCE":
    case "GENERAL":
    default:
      return { kind: "system" as const, code: "system" };
  }
}
function infrastructureProjectAsset(project: CampaignInfrastructureOverview["projects"]["active"][number]) {
  switch (project.kind) {
    case "CONSTRUCTION":
      return { kind: "resource" as const, code: "materials" };
    case "SERVICE":
      return { kind: "institution" as const, code: "governance" };
    case "SUPPLY":
      return { kind: "resource" as const, code: "food" };
    case "REFORM":
      return { kind: "institution" as const, code: "governance" };
    case "RESEARCH":
      return { kind: "resource" as const, code: "information" };
    case "DEFENSE":
      return { kind: "resource" as const, code: "security" };
    case "GENERAL":
    default:
      return { kind: "system" as const, code: "system" };
  }
}
function continuityAsset(type: CampaignContinuityItem["type"]) {
  switch (type) {
    case "PROJECT":
      return { kind: "system" as const, code: "system" };
    case "AGREEMENT":
      return { kind: "faction" as const, code: "citizens" };
    case "PROMISE":
      return { kind: "goal" as const, code: "goal" };
    case "POLICY":
      return { kind: "institution" as const, code: "governance" };
    case "STORY_THREAD":
    default:
      return { kind: "system" as const, code: "system" };
  }
}
function AssetBadge({
  kind,
  code,
  label,
  className,
}: {
  kind: AssetBadgeKind;
  code?: string;
  label: string;
  className?: string;
}) {
  const [fallback, setFallback] = useState(false);
  const slug = kind === "dossier"
    ? "dossier"
    : inferWorldAssetSlug(kind, code, label);
  const primary = kind === "goal" || kind === "system"
    ? `${GAME_UI_ASSET_BASE}/icon_world_${kind}.png`
    : kind === "dossier"
      ? `${GAME_UI_ASSET_BASE}/icon_dossier.png`
      : `${GAME_UI_ASSET_BASE}/icon_world_${kind}_${slug}.png`;
  const fallbackSrc = `${GAME_UI_ASSET_BASE}/${WORLD_ASSET_FALLBACKS[kind]}`;
  return (
    <span className={className ?? "cc-icon-badge cc-icon-badge-image"} aria-hidden="true">
      <img
        src={fallback ? fallbackSrc : primary}
        alt=""
        onError={() => setFallback(true)}
      />
    </span>
  );
}
function WorldAssetIcon({
  kind,
  code,
  label,
}: {
  kind: WorldAssetKind;
  code?: string;
  label: string;
}) {
  const [fallback, setFallback] = useState(false);
  const slug = inferWorldAssetSlug(kind, code, label);
  const primary = kind === "goal" || kind === "system"
    ? `${GAME_UI_ASSET_BASE}/icon_world_${kind}.png`
    : `${GAME_UI_ASSET_BASE}/icon_world_${kind}_${slug}.png`;
  const fallbackSrc = `${GAME_UI_ASSET_BASE}/${WORLD_ASSET_FALLBACKS[kind]}`;
  return (
    <span className={`cc-world-asset-icon ${kind}`} aria-hidden="true">
      <img
        src={fallback ? fallbackSrc : primary}
        alt=""
        onError={() => setFallback(true)}
      />
      <i>{label.slice(0, 1).toUpperCase()}</i>
    </span>
  );
}
function WorldSectionAssetHeader({
  eyebrow,
  title,
  kind,
  code,
}: {
  eyebrow: string;
  title: string;
  kind: WorldAssetKind;
  code?: string;
}) {
  return (
    <div className="cc-block-header cc-world-asset-header">
      <WorldAssetIcon kind={kind} code={code} label={title} />
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
      </div>
    </div>
  );
}
function worldAssetSlug(value: string | undefined) {
  const normalized = (value ?? "")
    .toLowerCase()
    .replace(/ё/g, "e")
    .normalize("NFKD")
    .replace(/[^a-z0-9а-я]+/gu, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || "generic";
}

export {
  WORLD_ASSET_FALLBACKS,
  inferWorldAssetSlug,
  infrastructureCategoryAsset,
  infrastructureProjectAsset,
  continuityAsset,
  AssetBadge,
  WorldAssetIcon,
  WorldSectionAssetHeader,
  worldAssetSlug,
};
export type { AssetBadgeKind, WorldAssetKind };
